import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { chatActionService } from "../../services/api";

const toKey = (value) => (value !== null && value !== undefined ? String(value) : null);

const ReassignAdminModal = ({
  isOpen,
  onClose,
  pitchId,
  cid,
  channelId,
  channel,
  currentAdminAccountId,
  currentAccountId,
  preselectAccountId,
  title,
  description,
  confirmLabel,
  onAdminReassigned,
}) => {
  // Pitch channels keep the pitch endpoint; direct_* channels reassign by cid.
  const usePitch = Boolean(pitchId);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [error, setError] = useState(null);
  const [selectedAdminId, setSelectedAdminId] = useState(null);

  const normalizedCurrentAdminId = toKey(currentAdminAccountId);
  const normalizedCurrentUserId = toKey(currentAccountId);

  useEffect(() => {
    if (!isOpen) {
      setStaffOptions([]);
      setError(null);
      setSelectedAdminId(null);
      return;
    }

    let cancelled = false;

    const fetchStaff = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = usePitch
          ? await chatActionService.getPitchStaff(pitchId)
          : await chatActionService.getChannelStaff(cid);

        if (cancelled) {
          return;
        }

        if (!response.success) {
          throw new Error(response.error || "Failed to load staff");
        }

        setStaffOptions(response.results || []);
      } catch (err) {
        console.error("ReassignAdminModal: fetch error", err);
        if (!cancelled) {
          setError(err.message || "Failed to load staff");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchStaff();

    return () => {
      cancelled = true;
    };
  }, [isOpen, pitchId, cid, usePitch]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, isSubmitting, onClose]);

  const channelMembers = useMemo(
    () => staffOptions.filter((staff) => Boolean(staff.already_in_channel)),
    [staffOptions]
  );

  const eligibleMembers = useMemo(() => {
    return channelMembers.filter((staff) => {
      const staffIdKey = toKey(staff.account_id);
      if (!staffIdKey || staffIdKey === normalizedCurrentAdminId) {
        return false;
      }
      return Boolean(staff.can_contact_agents);
    });
  }, [channelMembers, normalizedCurrentAdminId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!selectedAdminId && eligibleMembers.length > 0) {
      // Prefer the row the "Make admin" affordance was invoked on, if eligible.
      const preselected = eligibleMembers.find(
        (staff) => toKey(staff.account_id) === toKey(preselectAccountId),
      );
      setSelectedAdminId(
        (preselected || eligibleMembers[0]).account_id,
      );
    }
  }, [isOpen, eligibleMembers, selectedAdminId, preselectAccountId]);

  const handleSubmit = async () => {
    if (!pitchId && !cid) {
      toast.error("Missing channel information");
      return;
    }

    if (!selectedAdminId) {
      toast.error("Select a staff member to assign as chat admin");
      return;
    }

    const newAdminIdNumber = Number(selectedAdminId);
    const newAdminAccountId = Number.isNaN(newAdminIdNumber)
      ? selectedAdminId
      : newAdminIdNumber;

    setIsSubmitting(true);

    try {
      const response = usePitch
        ? await chatActionService.reassignPitchAdmin(pitchId, {
            channel_id: channelId,
            new_admin_account_id: newAdminAccountId,
          })
        : await chatActionService.reassignChannelAdmin(cid, newAdminAccountId);

      if (!response.success) {
        throw new Error(response.error || "Failed to change chat admin");
      }

      const effectiveNewAdminId =
        response.new_admin_account_id ?? newAdminAccountId;

      toast.success("Chat admin updated successfully");

      try {
        await channel?.queryMembers?.({}, {}, { limit: 30 });
      } catch (refreshError) {
        console.warn(
          "ReassignAdminModal: failed to refresh channel members",
          refreshError
        );
      }

      onAdminReassigned?.(effectiveNewAdminId);
      onClose();
    } catch (err) {
      console.error("ReassignAdminModal: submit error", err);
      toast.error(err.message || "Failed to change chat admin");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={isSubmitting ? undefined : onClose}
        aria-hidden="true"
      />

      {/* #34: bottom sheet under md, centered dialog from md up. */}
      <div className="relative bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-xl md:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="md:hidden flex justify-center pt-2.5 pb-1">
          <span className="h-1 w-10 rounded-full bg-[#E6EAF2]" />
        </div>
        <div className="px-6 pt-6 pb-4 border-b border-[#E5E7EB]">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[#111927]">
                {title || "Change chat admin"}
              </h3>
              <p className="text-sm text-[#6C737F] mt-1">
                {description ||
                  "Assign a different staff member to manage this conversation."}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-[#6C737F] hover:text-[#111927] transition-colors disabled:opacity-50"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-3 text-xs text-[#6C737F]">
            Current admin account ID: {normalizedCurrentAdminId || "Unknown"}
          </div>
        </div>

        <div className="px-6 py-5">
          {isLoading ? (
            <div className="py-10 text-center text-sm text-[#6C737F]">
              Loading staff members...
            </div>
          ) : error ? (
            <div className="py-10 text-center text-sm text-red-600">{error}</div>
          ) : channelMembers.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#6C737F]">
              No club staff are associated with this pitch chat yet.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[#111927]">
                Select a staff member who already participates in this chat and has permission to contact agents.
              </p>
              <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {channelMembers.map((staff) => {
                  const staffIdKey = toKey(staff.account_id);
                  const isEligible =
                    staffIdKey &&
                    staffIdKey !== normalizedCurrentAdminId &&
                    Boolean(staff.can_contact_agents);
                  const isSelected =
                    toKey(selectedAdminId) === staffIdKey;

                  return (
                    <li
                      key={staffIdKey || `${staff.email}-${staff.name}`}
                      className={`border rounded-xl px-4 py-3 flex items-start gap-3 transition-colors ${
                        isEligible
                          ? isSelected
                            ? "border-[#2283FF] bg-[#F4F8FF]"
                            : "border-[#E5E7EB] hover:border-[#D0D5DD]"
                          : "border-[#FEE4E2] bg-[#FEF3F2]"
                      }`}
                    >
                      <label className={`flex items-start gap-3 w-full ${isEligible ? "cursor-pointer" : "cursor-not-allowed"}`}>
                        <input
                          type="radio"
                          name="new-admin"
                          className="mt-1"
                          value={staffIdKey || ""}
                          checked={isSelected}
                          onChange={() => {
                            if (isEligible) {
                              setSelectedAdminId(staff.account_id);
                            }
                          }}
                          disabled={!isEligible || isSubmitting}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-[#111927]">
                            {staff.name || staff.email || `Account ${staffIdKey}`}
                          </div>
                          <div className="text-xs text-[#6C737F] mt-1">
                            {staff.email || "Email not available"}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            {staffIdKey === normalizedCurrentAdminId && (
                              <span className="px-2 py-1 bg-[#F2F4F7] text-[#344054] rounded-full">
                                Current admin
                              </span>
                            )}
                            {staff.can_contact_agents ? (
                              <span className="px-2 py-1 bg-[#ECFDF3] text-[#027A48] rounded-full">
                                Can contact agents
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-[#FEF3F2] text-[#B42318] rounded-full">
                                Cannot contact agents
                              </span>
                            )}
                            {staffIdKey === normalizedCurrentUserId && (
                              <span className="px-2 py-1 bg-[#E0EAFF] text-[#1C64F2] rounded-full">
                                You
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
              {eligibleMembers.length === 0 && (
                <div className="border border-[#FEE4E2] bg-[#FEF3F2] rounded-xl px-4 py-3 text-sm text-[#B42318]">
                  No eligible staff members available. Invite or enable agent-contact permissions for another staff member before changing the admin.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] bg-[#F9FAFB] rounded-b-2xl flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-[#111927] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F4F4F4] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              isLoading ||
              !!error ||
              eligibleMembers.length === 0 ||
              !selectedAdminId
            }
            className="px-4 py-2 text-sm font-medium text-white bg-[#2283FF] rounded-lg hover:bg-[#1a6acc] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {confirmLabel || "Change admin"}
          </button>
        </div>
      </div>
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return createPortal(modalContent, document.body);
};

export default ReassignAdminModal;
